import { getErrorData } from '@app/services/error.service';
import { readLocalStorageData, saveLocalStorageData } from '@app/services/localStorage.service';
import { navigateTo } from '@app/services/redirectService';
import { LocalStorageItems } from '@app/shared/constants/localStorageItems';
import axios from 'axios';

const REST_URL = '/api/';

const maintenanceErrors = [
	'Начало сеанса с информационной базой запрещено.',
	'Startup of infobase session is not allowed.',
	'Початок сеансу з інформаційною базою заборонений.',
];

/**
 * Unauthenticated endpoints where a 401 answers the question that was asked
 * ("no such user", "user disabled") instead of signalling an expired session.
 * Logging the visitor out here would bounce them off the login screen and hide
 * the real error.
 */
const publicAuthEndpoints = ['Login', 'LoginAccept', 'LoginPre', 'LoginAuth', 'Onboarding'];

let controller = new AbortController();
let hasShown401 = false;

/**
 * Axios instance.
 */
export const httpApi = axios.create({
	baseURL: REST_URL,
	timeout: 60000,
});

httpApi.interceptors.request.use((config) => {
	config.signal = controller.signal;

	// The session travels in a header, never in the URL — matches the FW-mobile client.
	const session = readLocalStorageData(LocalStorageItems.sessionID);
	if (session) config.headers.set('session', session);

	return config;
});

/**
 * Centralized API error handler.
 */
httpApi.interceptors.response.use(
	(res) => res,
	(err) => {
		const responseMessage = err.response?.data || '';
		const responseStatus = err.response?.status || 500;

		// Prefix match, not equality: `Onboarding` carries the code in the path
		// (`Onboarding/3126501237/`). The Login* entries are bare names, so this
		// matches them exactly as before.
		const requestUrl = err.config?.url ?? '';
		const isPublicAuthEndpoint = publicAuthEndpoints.some((endpoint) =>
			requestUrl.startsWith(endpoint),
		);

		// Abort all pending requests once we face 401.
		if (responseStatus === 401 && !isPublicAuthEndpoint) {
			if (!hasShown401) {
				hasShown401 = true;
			}

			// Only capture the redirect target from a real (non-auth) page. Later 401s
			// fire after we've navigated to /logout or /auth/login — saving those would
			// clobber the original deep-link URL (incl. its query params).
			const { pathname, search } = window.location;
			const isAuthRoute = pathname.startsWith('/auth') || pathname === '/logout';
			if (!isAuthRoute) {
				saveLocalStorageData(LocalStorageItems.redirectUrl, pathname + search);
			}
			navigateTo('/logout');

			controller.abort();
			controller = new AbortController();
		}

		// Catch maintenance mode and redirect to page.
		if (responseStatus === 500) {
			if (
				typeof responseMessage === 'string' &&
				maintenanceErrors.some((m) => responseMessage.includes(m))
			) {
				controller.abort();
				controller = new AbortController();
				navigateTo('/maintenance');
			}
		}

		return Promise.reject(getErrorData(err));
	},
);

/**
 * Base query params for every request.
 *
 * `session` is required here: the 1C backend reads it from the query string and
 * treats a request without it as anonymous, answering 401 — which the response
 * interceptor turns into a logout. It was dropped in `b4abdb9` when the header
 * approach was ported over from the FW-mobile client; mobile's backend accepts
 * the header, the web one does not. The request interceptor still sets the
 * header too, which is harmless and keeps the two clients aligned.
 */
export const getQueryParams = () => ({
	client: 'site3.1',
	session: readLocalStorageData(LocalStorageItems.sessionID),
	lang: readLocalStorageData(LocalStorageItems.language),
});
